import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000
      );

      const response = await apiClient.get(`/blogs`, {
        cancelToken: source.token,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (Array.isArray(response.data)) {
        setBlogs(response.data);
      } else {
        throw new Error("Invalid data format received from server");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(new Error("Request timeout: Please check your connection"));
      } else if (err.response) {
        setError(
          new Error(
            `Server Error: ${err.response.status} - ${
              err.response.data?.message || "Unknown error"
            }`
          )
        );
      } else if (err.request) {
        setError(
          new Error(
            "Network Error: Unable to reach the server. Please check your connection."
          )
        );
      } else {
        setError(new Error(`Error: ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des articles...</p>
        </div>
      </div>
    );
  }

  // Show full page error if no blogs data
  if (error && blogs.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement des articles</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i>
                      Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dynamic JSON-LD structured data for blogs
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "DVELGE Group Blog",
    url: "https://www.dvelgegroup.com/blogs",
    description:
      "Dernières actualités, conseils et articles sur la technologie, la transformation digitale et les formations IT.",
    blogPost: blogs.map((b) => ({
      "@type": "BlogPosting",
      headline: b.titre_blog,
      image:
        b.image_path || "https://www.dvelgegroup.com/images/blogs/default.jpg",
      author: {
        "@type": "Organization",
        name: "DVELGE Group",
      },
      datePublished: b.created_at,
      description: b.description_blog,
      url: `https://www.dvelgegroup.com/blog-details/${b.id_blog}`,
    })),
  };

  return (
    <div>
      <Helmet>
        <title>DVELGE Group | Blog & Actualités</title>
        <meta
          name="description"
          content="Consultez le blog de DVELGE Group pour les dernières actualités sur la transformation digitale, développement logiciel et formations IT en Guinée."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="DVELGE Group | Blog & Actualités" />
        <meta
          property="og:description"
          content="Articles et actualités sur la technologie, la transformation digitale et les formations IT."
        />
        <meta property="og:url" content="https://www.dvelgegroup.com/blogs" />
        <meta
          property="og:image"
          content="https://www.dvelgegroup.com/images/blogs/default.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DVELGE Group | Blog & Actualités" />
        <meta
          name="twitter:description"
          content="Articles et actualités sur la technologie, la transformation digitale et les formations IT."
        />
        <meta
          name="twitter:image"
          content="https://www.dvelgegroup.com/images/blogs/default.jpg"
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Section d'en-tête */}
      <div className="r-bg-a pt85 pb120">
        <div className="container">
          <div className="row pt80">
            <div className="col-lg-5">
              <div className="page-headings">
                <span className="sub-heading mb15">
                  <i className="fas fa-book mr5"></i> Blogs et Actualités
                </span>
                <h1 className="mb15">
                  Notre <span className="ree-text rt40"> Blog</span>
                </h1>
                <p>Qu'aimeriez-vous apprendre aujourd'hui ?</p>

                {/* Show warning if there was an error but we have cached data */}
                {error && blogs.length > 0 && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Données en cache: {error.message}
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={handleRetry}
                        disabled={loading}
                      >
                        {loading ? "Chargement..." : "Actualiser"}
                      </button>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section des blogs */}
      <div className="blog-block sec-pad pt80">
        <div className="container">
          <div className="blog-post">
            {/* Loading state for blogs section during retry */}
            {loading && retryCount > 0 ? (
              <div className="row justify-content-center">
                <div className="col-12 text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-3">Mise à jour des articles...</p>
                </div>
              </div>
            ) : (
              <div className="row">
                {blogs.length > 0 ? (
                  blogs.map((blog) => (
                    <div className="col-lg-4 col-sm-6" key={blog.id_blog}>
                      <div className="ree-media-crd">
                        <div className="rpl-img">
                          <Link to={`/blog-details/${blog.id_blog}`}>
                            <img
                              src={
                                blog.image_path || "images/blogs/default.jpg"
                              }
                              alt={blog.titre_blog}
                              className="fill-fixed"
                              onError={(e) => {
                                e.target.src = "/images/blogs/default.jpg";
                                e.target.alt = "Image du blog non disponible";
                              }}
                            />
                          </Link>
                        </div>
                        <div className="rpl-contt">
                          <div className="blog-quick-inf mb20 mt30">
                            <span>
                              <i className="far fa-calendar-alt"></i>{" "}
                              {blog.created_at
                                ? new Date(blog.created_at).toLocaleDateString(
                                    "fr-FR"
                                  )
                                : "Date non disponible"}
                            </span>
                            <span>
                              <i className="fas fa-clock"></i>{" "}
                              {blog.readTime || "5 min de lecture"}
                            </span>
                          </div>
                          <h4>
                            <Link to={`/blog-details/${blog.id_blog}`}>
                              {blog.titre_blog}
                            </Link>
                          </h4>
                          <p>
                            {blog.description_blog ||
                              "Aucune description disponible."}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <div className="text-center">
                      <i className="fas fa-newspaper fa-3x text-muted mb-3"></i>
                      <h4 className="text-muted">Aucun article disponible</h4>
                      <p className="text-muted mb-4">
                        Nous n'avons pas d'articles à afficher pour le moment.
                      </p>
                      <button className="btn btn-primary" onClick={handleRetry}>
                        <i className="fas fa-redo me-2"></i>
                        Réessayer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pagination - Only show if we have blogs */}
            {blogs.length > 0 && (
              <div className="col-lg-12 col-sm-12 mt100">
                <ul className="pagination shadows">
                  <li>
                    <a href="#" className="prev">
                      Précédent
                    </a>
                  </li>
                  <li className="pageNumber active">
                    <a href="#">1</a>
                  </li>
                  <li className="pageNumber">
                    <a href="#">2</a>
                  </li>
                  <li className="pageNumber">
                    <a href="#">3</a>
                  </li>
                  <li className="pageNumber">
                    <a href="#">4</a>
                  </li>
                  <li>
                    <a href="#" className="next">
                      Suivant
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
